import { Component, Input } from '@angular/core';
import { FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, switchMap } from 'rxjs';
import { PostalCodeService } from '../../services/postal-code.service';

type PostalCodeLookupStatus = 'idle' | 'complete' | 'partial' | 'notFound';

@Component({
  selector: 'split-address-form',
  templateUrl: './split-address-form.component.html',
  styleUrls: ['./split-address-form.component.scss']
})
export class SplitAddressFormComponent {
  protected listState: any[] = this.postalCodeService.states;

  protected listCity: any[] = [];

  protected validators = Validators;

  protected form!: FormGroup;

  @Input() public formGroupName!: string;
  @Input() public showEmail = false;
  @Input() public useRevenuePostalCodeRules = false;
  @Input() public cityStateFieldType: 'dropdown' | 'input' = 'dropdown';

  protected loading: boolean = false;
  protected postalCodeLookupStatus: PostalCodeLookupStatus = 'idle';
  private postalCodeLookupPerformed = false;

  constructor(
    private formGroupDirective: FormGroupDirective,
    private postalCodeService: PostalCodeService
  ) {}

  ngOnInit(): void {
    this.form = this.formGroupDirective.control.get(this.formGroupName) as FormGroup;
    this.applyRevenuePostalCodeLocks();

    this.form.get("ibge")?.valueChanges.subscribe(
      (ibge: string) => {
        let city = this.listCity.find((city: any) => city.id == ibge);

        if(city) {
          this.form.get("city")?.setValue(city.nome); 
        }
      }
    );

    this.form.get("postalCode")?.valueChanges.pipe(
      debounceTime(1000),
      distinctUntilChanged(),
      switchMap(
        (value: any) => {
          const postalCode = this.onlyNumbers(value);

          if (this.useRevenuePostalCodeRules && this.postalCodeLookupStatus == 'notFound') {
            this.postalCodeLookupStatus = 'idle';
          }

          if (this.useRevenuePostalCodeRules) {
            this.clearPostalCodeNotFoundError();
            this.applyRevenuePostalCodeLocks();
          }

          if(!postalCode) {
            if (this.useRevenuePostalCodeRules) {
              this.postalCodeLookupStatus = 'idle';
              this.postalCodeLookupPerformed = false;
              this.clearAddressAutocompleteFields();
              this.applyRevenuePostalCodeLocks();
            }
            return EMPTY;
          }

          if(postalCode.length < 8) {
            if (this.useRevenuePostalCodeRules) {
              this.postalCodeLookupStatus = 'idle';
              this.postalCodeLookupPerformed = false;
              this.applyRevenuePostalCodeLocks();
            }
            return EMPTY;
          }

          this.loading = true;
          return this.postalCodeService.getPostalCode(postalCode).pipe(
          catchError(error => {
            this.loading = false;
            if (this.useRevenuePostalCodeRules) {
              this.postalCodeLookupStatus = 'notFound';
              this.postalCodeLookupPerformed = true;
              this.clearAddressAutocompleteFields();
              this.form.get("postalCode")?.setErrors({ ...(this.form.get("postalCode")?.errors ?? {}), cepNotFound: true });
              this.applyRevenuePostalCodeLocks();
            } else {
              this.form.get("streetAddress")?.setValue("");
              this.form.get("district")?.setValue("");
              this.form.get("state")?.setValue("");
              this.form.get("ibge")?.setValue("");
              this.form.get("city")?.setValue("");
            }
            return EMPTY;
          }))
        }
      )
    ).subscribe(
      (value) => {
        this.loading = false;
        if (this.useRevenuePostalCodeRules) {
          const isComplete = !!value.streetAddress && !!value.district && !!value.city && !!value.state;
          this.postalCodeLookupStatus = isComplete ? 'complete' : 'partial';
          this.postalCodeLookupPerformed = true;
          this.clearPostalCodeNotFoundError();
          this.form.get("streetAddress")?.setValue(isComplete ? value.streetAddress : "");
          this.form.get("district")?.setValue(isComplete ? value.district : "");
        } else {
          this.form.get("streetAddress")?.setValue(value.streetAddress);
          this.form.get("district")?.setValue(value.district);
        }
        this.form.get("state")?.setValue(value.state);
        this.form.get("ibge")?.setValue(value.ibge);
        this.form.get("city")?.setValue(value.city);
        this.form.get("country")?.setValue("Brasil");
        this.applyRevenuePostalCodeLocks();
        this.getCitiesByState(value.uf);
      }
    );

    if(this.form.get("state")?.value) {
      this.getCitiesByState(this.form.get("state")?.value);
    }
  }

  getCitiesByState(state: string) {
    this.postalCodeService.getCitiesByState(state)
        .subscribe((cities: any) => {
            this.listCity = cities;
        });
  }

  protected get postalCodeHelperText(): string {
    if (this.postalCodeLookupStatus == 'complete') {
      return 'Dados preenchidos automaticamente pelo CEP.';
    }

    if (this.postalCodeLookupStatus == 'partial') {
      return 'CEP localizado parcialmente. Complete os dados restantes.';
    }

    return '';
  }

  protected get isPostalCodeNotFound(): boolean {
    return this.postalCodeLookupStatus == 'notFound';
  }

  protected isFieldReadonly(field: string): boolean {
    if (!this.useRevenuePostalCodeRules) return false;
    if (field == 'number' || field == 'address2' || field == 'email') return false;
    if (field == 'streetAddress' || field == 'district') return !this.postalCodeLookupPerformed || this.shouldLockFullPostalCodeFields();
    if (field == 'state' || field == 'city') return !this.postalCodeLookupPerformed || this.shouldLockCityStateFields();
    return false;
  }

  private applyRevenuePostalCodeLocks(): void {
    if (!this.useRevenuePostalCodeRules || !this.form || this.form.disabled) return;

    ['streetAddress', 'district', 'state', 'city', 'number', 'address2', 'email'].forEach((field) => {
      const control = this.form.get(field);
      if (!control) return;

      if (this.isFieldReadonly(field)) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });
  }

  private shouldBlockAddressFieldsByPostalCode(): boolean {
    return !this.hasCompletePostalCode() || this.isPostalCodeNotFound;
  }

  private shouldLockFullPostalCodeFields(): boolean {
    return this.shouldBlockAddressFieldsByPostalCode() || this.postalCodeLookupStatus == 'complete';
  }

  private shouldLockCityStateFields(): boolean {
    return this.shouldBlockAddressFieldsByPostalCode() ||
      this.postalCodeLookupStatus == 'complete' ||
      this.postalCodeLookupStatus == 'partial';
  }

  private hasCompletePostalCode(): boolean {
    return this.onlyNumbers(this.form.get('postalCode')?.value).length == 8;
  }

  private clearAddressAutocompleteFields(): void {
    this.form.get("streetAddress")?.setValue("");
    this.form.get("district")?.setValue("");
    this.form.get("state")?.setValue("");
    this.form.get("ibge")?.setValue("");
    this.form.get("city")?.setValue("");
    this.form.get("number")?.setValue("");
  }

  private clearPostalCodeNotFoundError(): void {
    const postalCodeControl = this.form.get("postalCode");
    const errors = postalCodeControl?.errors;

    if (!errors?.['cepNotFound']) {
      return;
    }

    delete errors['cepNotFound'];
    postalCodeControl?.setErrors(Object.keys(errors).length ? errors : null);
  }

  private onlyNumbers(value: any): string {
    return String(value ?? '').replace(/\D/g, '');
  }
}
